import React from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import { useNavigate, useLocation } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { formatDateDDMMYYYY } from "../../utils/dateUtils";

const ViewMoreKnowledge = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const article = location.state;

  const goBack = () => {
    navigate("/knowledge");
  };

  if (!article) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6">No article data available</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ mt: 2 }}>
          Back to Knowledge Base
        </Button>
      </Box>
    );
  }

  const targetLabels = {
    "1": "Client",
    "2": "Employee",
    "3": "App User",
  };

  const userTypeLabels = {
    "1": "Driver",
    "2": "Owner",
    "3": "Dhaba",
    "4": "Mechanic",
    "5": "Crane Owner",
    "6": "Crane Operator",
  };

  const statusLabels = {
    "1": "Active",
    "0": "Inactive",
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return formatDateDDMMYYYY(dateString);
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#1976d2" }}>
            📖 Article Details
          </Typography>
        </Box>
      </Paper>

      {/* Article Info */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: "white",
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "#1976d2" }}>
          📋 Article Information
        </Typography>

        <Stack spacing={3}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ minWidth: 200, fontWeight: 500, color: "#666" }}>
              Article Title:
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: "1.1rem", color: "#333" }}>
              {article.title}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ minWidth: 200, fontWeight: 500, color: "#666" }}>
              Category:
            </Typography>
            <Chip
              label={article.article || "N/A"}
              sx={{
                backgroundColor: "#e3f2fd",
                color: "#1976d2",
                fontWeight: 500,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ minWidth: 200, fontWeight: 500, color: "#666" }}>
              Target Audience:
            </Typography>
            <Chip
              label={targetLabels[article.for] || "Unknown"}
              sx={{
                backgroundColor:
                  article.for === "1"
                    ? "#ffcdd2"
                    : article.for === "2"
                    ? "#d1ecf1"
                    : "#d5f5e3",
                color: article.for === "1" ? "#7f8c8d" : article.for === "2" ? "#0c5460" : "#7f8c8d",
                fontWeight: 500,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ minWidth: 200, fontWeight: 500, color: "#666" }}>
              User Type:
            </Typography>
            <Chip
              label={userTypeLabels[article.user_type] || "Unknown"}
              sx={{
                backgroundColor: "#c8e6c9",
                color: "#1b5e20",
                fontWeight: 500,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ minWidth: 200, fontWeight: 500, color: "#666" }}>
              Status:
            </Typography>
            <Chip
              label={statusLabels[article.status] || "Unknown"}
              sx={{
                backgroundColor: article.status === "1" ? "#c8e6c9" : "#ffcdd2",
                color: article.status === "1" ? "#1b5e20" : "#7f8c8d",
                fontWeight: 500,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ minWidth: 200, fontWeight: 500, color: "#666" }}>
              Created By:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon sx={{ color: "#1976d2" }} />
              <Typography sx={{ fontWeight: 500, color: "#333" }}>
                {article.creator_name || "Unknown"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ minWidth: 200, fontWeight: 500, color: "#666" }}>
              Created Date:
            </Typography>
            <Typography sx={{ fontWeight: 500, color: "#333" }}>
              {formatDate(article.created_at)}
            </Typography>
          </Box>

          {article.file && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ minWidth: 200, fontWeight: 500, color: "#666" }}>
                Attached File:
              </Typography>
              <Button
                startIcon={<AttachFileIcon />}
                href={`https://rptpl.roadpilot.co.in/public/${article.file}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                }}
              >
                {article.file.split("/").pop()}
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Description */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: "white",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}>
          📝 Description
        </Typography>
        <ReactQuill
          theme="snow"
          value={article.description || ""}
          readOnly={true}
          style={{ minHeight: 300, color: 'black' }}
          modules={{ toolbar: false }}
          formats={[]}
        />
        <style>{`
          .ql-editor {
            color: black !important;
          }
          .ql-snow .ql-editor {
            color: black !important;
          }
        `}</style>
      </Paper>
    </Box>
  );
};

export default ViewMoreKnowledge;
